import {
  RadioProps as MUIRadioProps,
  Radio as MUIRadio,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';

import { fontSize } from '@verantos-dev/shared-ui-components/tokens';

import { useRadioButtonLocators } from './hooks/useRadioButtonLocators';
import { StyledContainer } from './styledComponents/StyledContainer';
import { TheRadioButton } from './styledComponents/StyledRadioButton';
import { StyledRadioButtonLabel } from './styledComponents/StyledRadioButtonLabel';
import { RadioButtonSize } from './types';
import { InputComponentWidth } from '../../types';
import { getWidth } from '../../utils/getWidth';

export type LocalRadioButtonProps = {
  /**
   * @prop {string} id - An unique identifier for the radio button
   * Used to distinguish this radio button from others in the application.
   */
  id: string;

  /**
   * @prop {string} text - label for the radio button
   */
  label: string;

  /**
   * @prop {RadioButtonSize} displayLabelSize - size of the label
   * @default 'normal'
   */
  labelSize?: RadioButtonSize;

  /**
   * @prop {InputComponentWidth} width - the width of the checkbox and label
   * @default 'medium'
   */
  width?: InputComponentWidth;

  /**
   * @prop showBorderBox - whether to show the border box
   * @default false
   */
  showBorderBox?: boolean;

  /**
   * @prop {callback} onChange - click handler
   * @param {string} id - id of the button
   * @param {boolean} checked - the selected value
   */
  onChange?: (id: string, checked: boolean) => void;

  /**
   * @prop {SxProps<Theme>} labelSx - sx props for the label
   */
  labelSx?: SxProps<Theme>;

  /**
   * @prop {TypographyProps} labelTypographyProps - props for the label Typography component
   */
  labelTypographyProps?: TypographyProps;
};

export type RadioButtonProps = LocalRadioButtonProps & Omit<MUIRadioProps, 'onChange'>;

export const RadioButton = (props: RadioButtonProps) => {
  const {
    id,
    label,
    labelSize = RadioButtonSize.NORMAL,
    disabled = false,
    checked = false,
    width = 'medium',
    showBorderBox = false,
    onChange,
    labelSx = {},
    labelTypographyProps = {},
    ...rest
  } = props;

  const theme = useTheme();

  const { containerNodeId, radioNodeId, labelNodeId } = useRadioButtonLocators({ key: id });

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange && onChange(id, e.target.checked);
  };

  return (
    <StyledContainer
      id={containerNodeId}
      aria-labelledby={containerNodeId}
      showBorderBox={showBorderBox}
      style={{ width: getWidth(width) }}
    >
      <TheRadioButton>
        <MUIRadio
          id={radioNodeId}
          value={id}
          checked={checked}
          disabled={disabled}
          aria-labelledby={labelNodeId}
          aria-checked={checked}
          aria-disabled={disabled}
          centerRipple={false}
          disableRipple
          {...rest}
          onChange={onChangeHandler}
          sx={{
            '& .MuiSvgIcon-root': {
              fontSize: fontSize[24],
            },
            ...rest.sx,
          }}
        />
      </TheRadioButton>

      <StyledRadioButtonLabel
        id={labelNodeId}
        htmlFor={radioNodeId}
        size={labelSize}
        disabled={disabled}
        sx={{ ...labelSx }}
      >
        <Typography
          variant="body1"
          color={theme.palette.text.primary}
          fontSize={
            labelSize === RadioButtonSize.SMALL
              ? '12px'
              : labelSize === RadioButtonSize.MEDIUM
              ? '14px'
              : '16px'
          }
          lineHeight={
            labelSize === RadioButtonSize.SMALL
              ? '12px'
              : labelSize === RadioButtonSize.MEDIUM
              ? '20px'
              : '24px'
          }
          letterSpacing={labelSize === RadioButtonSize.MEDIUM ? '0.17px' : '0.15px'}
          {...labelTypographyProps}
        >
          {label}
        </Typography>
      </StyledRadioButtonLabel>
    </StyledContainer>
  );
};
